import React, { useMemo } from 'react';
import { ReactComponent as CloseIcon } from '@blink/assets/src/icons/close.svg';

import { cn } from '@/shared/lib/tailwind-merge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogClose,
    DialogDescription,
} from '@/shared/ui';

interface BaseModalProps {
    open: boolean;
    onClose: () => void;
    title?: any;
    note?: string;
    className?: string;
    isClosable?: boolean;
    children: React.ReactNode;
    border?: boolean;
    description?: string;
    icon?: React.ReactNode;
}

export const BaseModal: React.FC<BaseModalProps> = ({
    open,
    onClose,
    title,
    note,
    className = '',
    isClosable = true,
    children,
    border = true,
    description = '',
    icon,
}) => {
    const { titleId, closeIconId } = useMemo(() => {
        let titleId = '';
        let closeIconId = '';

        if (title && typeof title === 'string') {
            titleId = title?.toLowerCase()?.replace(/ /g, '-');
            titleId += '-title-modal';

            closeIconId = title?.toLowerCase()?.replace(/ /g, '-');
            closeIconId += '-close-icon-modal';
        }

        return { titleId, closeIconId };
    }, [title]);

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent
                className={cn('z-[1000] max-w-fit pt-5 pb-7 overflow-auto max-h-[90vh]', className)}
            >
                {title && (
                    <DialogHeader
                        className={cn(
                            'relative pb-3',
                            border ? 'border-b border-border-stateDisable' : '',
                        )}
                    >
                        {icon || description ? (
                            <div className='flex'>
                                {icon && <div className='mt-3 mr-4'>{icon}</div>}

                                <div className='flex flex-col py-4 gap-3'>
                                    <DialogTitle
                                        data-testid={titleId}
                                        className='text-[20px] font-semibold font-[Inter] p-0'
                                    >
                                        {title}
                                    </DialogTitle>

                                    {description && (
                                        <DialogDescription className='text-[16px] font-normal font-[Inter] text-content-secondary'>
                                            {description}
                                        </DialogDescription>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <DialogTitle
                                data-testid={titleId}
                                className='text-[20px] font-semibold font-[Inter]'
                            >
                                {title}
                            </DialogTitle>
                        )}

                        {isClosable && (
                            <DialogClose
                                data-testid={closeIconId}
                                className={cn(
                                    '!mt-0 w-7 h-7 hover:opacity-80 transition-opacity',
                                    icon ? '!mt-4 self-baseline' : '',
                                )}
                            >
                                <CloseIcon />
                            </DialogClose>
                        )}
                    </DialogHeader>
                )}
                {note && (
                    <div className='text-[16px] leading-[20px] tracking-[-0.48px] text-content-secondary font-normal mt-3'>
                        {note}
                    </div>
                )}
                <div className='flex flex-col font-[Inter]'>{children}</div>
            </DialogContent>
        </Dialog>
    );
};
